/**
 * Referral URL generator for the CloudBTL "powered by" CTA.
 *
 * Appends attribution params (ref=<agencyId>, src=proposal_<proposalId>)
 * plus a deterministic signature (sig=) that lets the landing page detect
 * naive URL tampering.
 *
 * Signing uses FNV-1a 32-bit with a client-embedded key. This is NOT a
 * cryptographic secret — it is designed to catch accidental/naive param
 * mutations, not adversarial forgery.
 */

/** Client-side signing key — changing this version-bumps all existing sigs. */
const SIGNING_KEY = 'cloudbtl-referral-v1';

/**
 * FNV-1a 32-bit hash (non-cryptographic, deterministic, no deps).
 */
function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    // Math.imul keeps 32-bit multiplication without BigInt
    hash = (Math.imul(hash, 0x01000193) >>> 0);
  }
  return hash >>> 0; // ensure unsigned
}

/**
 * Produce a hex signature over the canonical payload string.
 * Payload format: `<SIGNING_KEY>|<ref>|<src>`
 */
function signPayload(ref: string, src: string): string {
  const payload = `${SIGNING_KEY}|${ref}|${src}`;
  return fnv1a32(payload).toString(16).padStart(8, '0');
}

/**
 * Build a referral URL for the "powered by CloudBTL" CTA.
 *
 * @param proposalId - ID of the proposal being viewed (e.g. "abc123")
 * @param agencyId   - ID of the sending agency/branding record
 * @returns Absolute URL like `https://cloudbtl.com?ref=<agency>&src=proposal_<id>&sig=<hex>`
 */
export function buildReferralUrl(proposalId: string, agencyId: string): string {
  const base = 'https://cloudbtl.com';
  const ref = agencyId;
  const src = `proposal_${proposalId}`;
  const sig = signPayload(ref, src);
  const params = new URLSearchParams({ ref, src, sig });
  return `${base}?${params.toString()}`;
}

/**
 * Verify that the `sig` param in a referral URL is consistent with the
 * `ref` and `src` params. Returns `false` if any param was tampered.
 *
 * @param ref - Value of the `ref` URL param
 * @param src - Value of the `src` URL param
 * @param sig - Value of the `sig` URL param
 */
export function verifyReferralSig(ref: string, src: string, sig: string): boolean {
  return signPayload(ref, src) === sig;
}

/**
 * Parse and verify a complete referral URL string.
 * Returns `null` when signature verification fails.
 */
export function parseReferralUrl(urlString: string): {
  ref: string;
  src: string;
  sig: string;
  valid: boolean;
} | null {
  try {
    const url = new URL(urlString);
    const ref = url.searchParams.get('ref') ?? '';
    const src = url.searchParams.get('src') ?? '';
    const sig = url.searchParams.get('sig') ?? '';
    if (!ref || !src || !sig) return null;
    return { ref, src, sig, valid: verifyReferralSig(ref, src, sig) };
  } catch {
    return null;
  }
}
